import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Plus, Minus } from 'lucide-react';

const formatCurrency = (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount);

export const OrderFormDialog = ({
  open, onOpenChange, onSubmit, formData, setFormData,
  rooms, menuItems, orderItems, addItemToOrder, updateItemQuantity, orderTotal
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="bg-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-['Manrope']">New Order</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label>Room (optional)</Label>
          <Select value={formData.room_number || 'walkin'} onValueChange={(value) => setFormData({...formData, room_number: value === 'walkin' ? '' : value})}>
            <SelectTrigger className="border-stone-300" data-testid="select-room-order"><SelectValue placeholder="Walk-in order" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="walkin">Walk-in order</SelectItem>
              {rooms.map(room => (<SelectItem key={room.id} value={room.room_number}>Room {room.room_number}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Add Items</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[200px] overflow-y-auto border border-stone-200 rounded-md p-3">
            {menuItems.map(item => (
              <button key={item.id} type="button" onClick={() => addItemToOrder(item)} className="text-left p-2 rounded border border-stone-200 hover:border-sage-300 hover:bg-sage-50 transition-colors" data-testid={`add-menu-item-${item.id}`}>
                <p className="font-medium text-sm text-stone-900 truncate">{item.name}</p>
                <p className="text-xs text-sage-700">{formatCurrency(item.price)}</p>
              </button>
            ))}
          </div>
        </div>

        {orderItems.length > 0 && (
          <div className="space-y-2">
            <Label>Order Items</Label>
            <div className="border border-stone-200 rounded-md divide-y divide-stone-100">
              {orderItems.map(item => (
                <div key={item.id} className="p-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900">{item.name}</p>
                    <p className="text-sm text-stone-500">{formatCurrency(item.price)} each</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => updateItemQuantity(item.id, -1)} className="h-8 w-8 p-0"><Minus className="w-4 h-4" /></Button>
                    <span className="w-8 text-center font-medium">{item.quantity}</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => updateItemQuantity(item.id, 1)} className="h-8 w-8 p-0"><Plus className="w-4 h-4" /></Button>
                    <span className="w-20 text-right font-medium text-stone-900">{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                </div>
              ))}
              <div className="p-3 bg-stone-50 flex items-center justify-between">
                <span className="font-semibold text-stone-900">Total</span>
                <span className="font-bold text-lg text-sage-700">{formatCurrency(orderTotal)}</span>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="notes">Notes</Label>
          <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Special instructions..." className="border-stone-300 min-h-[60px]" data-testid="order-notes-input" />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="border-stone-300">Cancel</Button>
          <Button type="submit" className="bg-sage-700 hover:bg-sage-800 text-white" disabled={orderItems.length === 0} data-testid="create-order-button">Create Order ({formatCurrency(orderTotal)})</Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);
